import {
  createContext,
  useEffect,
  EffectCallback,
  ReactNode,
  useState,
} from 'react'
import { useIsClient, useWindow } from '../hooks/useSSR'

export const ThemeContext = createContext(true)
export const ThemeUpdateContext = createContext<EffectCallback | undefined>(
  undefined
)

interface Props {
  children: ReactNode
}

export function ThemeProvider({ children }: Props) {
  const isClient = useIsClient()
  const windowObj = useWindow()
  const [prefersLight, setPrefersLight] = useState(true)
  const [lightTheme, setLightTheme] = useState(true)

  useEffect(() => {
    if (!isClient || !windowObj) return

    const mediaQuery = windowObj.matchMedia('(prefers-color-scheme: light)')
    const storedTheme = windowObj.localStorage.getItem('useLightMode')
    const parsedStoredTheme =
      storedTheme !== null ? (JSON.parse(storedTheme) as boolean) : null

    setPrefersLight(mediaQuery.matches)
    windowObj.localStorage.setItem(
      'prefersLight',
      JSON.stringify(mediaQuery.matches)
    )
    setLightTheme(parsedStoredTheme ?? mediaQuery.matches)

    const handleMediaQueryChange = (e: MediaQueryListEvent) => {
      setPrefersLight(e.matches)
      windowObj.localStorage.setItem('prefersLight', JSON.stringify(e.matches))
    }

    mediaQuery.addEventListener('change', handleMediaQueryChange)

    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange)
    }
  }, [isClient, windowObj])

  // Read the ?light query parameter and update theme
  useEffect(() => {
    if (!isClient || !windowObj) return
    const params = new URLSearchParams(windowObj.location.search)
    const lightParam = params.get('light')
    if (lightParam !== null) {
      const nextLightTheme = lightParam === 'true'
      setLightTheme(nextLightTheme)
      windowObj.localStorage.setItem(
        'useLightMode',
        JSON.stringify(nextLightTheme)
      )
    }
  }, [isClient, windowObj])

  const lightEnabled = lightTheme ?? prefersLight

  function toggleTheme() {
    setLightTheme((prevTheme) => {
      const nextTheme = !prevTheme

      if (windowObj) {
        windowObj.localStorage.setItem(
          'useLightMode',
          JSON.stringify(nextTheme)
        )
      }

      return nextTheme
    })
  }

  useEffect(() => {
    /*add 'light' class to body when lightEnabled is true*/
    if (!isClient || typeof document === 'undefined') return

    document?.body.classList.toggle('light', lightEnabled)
  }, [lightEnabled, isClient])

  return (
    <ThemeContext.Provider value={lightEnabled}>
      <ThemeUpdateContext.Provider value={toggleTheme}>
        {children}
      </ThemeUpdateContext.Provider>
    </ThemeContext.Provider>
  )
}
